import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useRouteMatch } from "react-router";
import { KLoader } from "@kyburz/kdesign-system";

import { setAlert } from "../../../redux/actions/actions";
import { formatErrorResponse } from "../../../utils/stringUtils";
import { FirmwareType, fwTypeMap, fwTypeOptionFor } from "../typings";
import { editFirmware, getFirmware } from "../../../apiCalls/config/firmware";
import { formatDate } from "../../../utils/timeUtils";
import DeviceFirmwareDetails from "../device-firmware-details/DeviceFirmwareDetails";

const EditFirmwarePage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [initialFirmware, setInitialFirmware] = useState<FirmwareType | null>(
    null,
  );

  const match = useRouteMatch<{ firmwareId?: string; tab?: string }>({
    path: "/config/devices/firmware/:firmwareId/*",
    strict: true,
    sensitive: true,
  });

  const { firmwareId } = match?.params || {};

  useEffect(() => {
    if (!firmwareId) return;
    getFirmware(firmwareId)
      .then((res: any) => {
        setInitialFirmware({
          id: res?.data?.id,
          hardwareType: res?.data?.hardware_type,
          firmwareType: fwTypeOptionFor(res?.data?.firmware_type).value,
          createdAt: formatDate(res?.data?.created_at),
          version: res?.data?.version,
          description: res?.data?.description,
          tags: res?.data?.tags || [],
          allowed_customers: res?.data?.allowed_customers,
          contentsBase64: res?.data?.contents?.Bytes,
        });
      })
      .catch(() => {
        dispatch(setAlert("Couldn't get device firmware", "error"));
      });
  }, [firmwareId]);

  const goBack = () => {
    history.push("/config/devices/firmware");
  };

  const handleEdit = async (updatedFirmware: FirmwareType) => {
    setLoading(true);
    const res = await editFirmware({
      ...updatedFirmware,
      contentsBase64:
        updatedFirmware?.contentsBase64?.split("base64,").pop() || null,
      firmwareType: fwTypeMap[updatedFirmware.firmwareType],
    });
    setLoading(false);
    if (res.data) {
      dispatch(
        setAlert(`Firmware ${updatedFirmware.version} edited`, "success"),
      );
      goBack();
    } else {
      dispatch(setAlert(formatErrorResponse(res), "error"));
    }
  };

  if (!initialFirmware) return <KLoader />;

  return (
    <DeviceFirmwareDetails
      initialFirmware={initialFirmware}
      title="Edit firmware"
      isLoading={loading}
      handleSave={handleEdit}
      handleCancel={goBack}
    />
  );
};

export default EditFirmwarePage;
